<template>
  <section v-if="isPending" class="py-24">
    <div class="mx-auto max-w-full px-24 md:max-w-[900px] md:px-32">
      <h2 class="heading-2 mb-8">{{ i18n(translations.title) }}</h2>

      <div class="flex justify-center py-40">
        <RevSpinner :alternative-text="i18n(translations.loading)" />
      </div>
    </div>
  </section>

  <section v-else-if="mappedOrderlines.length" class="py-24">
    <div class="mx-auto max-w-full px-24 md:max-w-[900px] md:px-32">
      <h2 class="heading-2 mb-8">{{ i18n(translations.title) }}</h2>

      <div class="mb-16 flex gap-8">
        <RevChip
          v-for="filter in ITEM_FILTERS"
          :key="filter.value"
          :label="i18n(filter.label)"
          :model-value="selectedFilter === filter.value"
          @click="selectedFilter = filter.value"
        />
      </div>

      <div class="flex gap-16 overflow-x-auto pb-4">
        <HelpCenterItemCard
          v-for="orderline in filteredOrderlines"
          :key="orderline.key"
          class="w-[233px] shrink-0"
          v-bind="orderline.cardProps"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevChip } from '@ds/components/Chip'
import { RevSpinner } from '@ds/components/Spinner'

import HelpCenterItemCard from '../HelpCenterItemCard/HelpCenterItemCard.vue'

import translations from './OrdersSection.translations'
import { MAX_VISIBLE_CARDS, compareByDateDesc } from './OrdersSection.utils'
import { useSaleCards } from './composables/useSaleCards'
import { useTradeInCards } from './composables/useTradeInCards'

const i18n = useI18n()

const { salesCards, isPending: isSalesPending } = useSaleCards()
const { tradeInCards, isPending: isTradeInsPending } = useTradeInCards()

const isPending = computed(
  () => isSalesPending.value || isTradeInsPending.value,
)

type CardType = 'sale' | 'trade-in'
type ItemFilter = 'all' | 'purchases' | 'trade-ins'

const FILTER_TO_CARD_TYPE: Record<Exclude<ItemFilter, 'all'>, CardType> = {
  purchases: 'sale',
  'trade-ins': 'trade-in',
}

const ITEM_FILTERS: Array<{
  value: ItemFilter
  label: (typeof translations)[keyof typeof translations]
}> = [
  { value: 'all', label: translations.filterAll },
  { value: 'purchases', label: translations.filterPurchases },
  { value: 'trade-ins', label: translations.filterTradeIns },
]

const selectedFilter = ref<ItemFilter>('all')

// Overall display cap: at most MAX_VISIBLE_CARDS cards are shown, sales and
// trade-ins combined (a trade-in can occupy a slot that would otherwise go
// to a sale). Replacement cards sort on their original's date, so groups
// stay adjacent after the merge. The cap can still cut a group at the
// boundary — known edge case, kept as follow-up.
const mappedOrderlines = computed(() =>
  [...salesCards.value, ...tradeInCards.value]
    .sort(compareByDateDesc)
    .slice(0, MAX_VISIBLE_CARDS),
)

const filteredOrderlines = computed(() => {
  if (selectedFilter.value === 'all') return mappedOrderlines.value

  const cardType = FILTER_TO_CARD_TYPE[selectedFilter.value]

  return mappedOrderlines.value.filter((card) => card.type === cardType)
})
</script>
